import { useFormContext, useWatch } from "react-hook-form";
import { X, Star, ArrowRight } from "lucide-react";
import { OfferForm } from "./offer.schema";
import { useOffersStore } from "@/src/stores/offersStore";
import { useStepsStore } from "./state";
import { calculateCurrencyPrice } from "../../../lib/utils";

/**
 * Shortlist rail (Phase 1, C1 choose step): a sticky strip of the yachts
 * already added to the offer — thumbnail, name, price, remove — with the
 * combined total. The first line is marked top pick (it leads the client
 * page). Reads/writes the offer form's yachts array directly, so it stays in
 * lockstep with the configure step and Summary.
 */
export function ShortlistRail({ sticky }: { sticky?: boolean } = {}) {
  const { control, setValue, getValues } = useFormContext<OfferForm>();
  const yachts = useWatch({ control, name: "yachts" }) ?? [];
  const currencyCode = useWatch({ control, name: "description.currency" }) ?? "EUR";
  const { currencies } = useOffersStore();
  const steps = useStepsStore();



  const toCurrency = (amount: number) => {
    const eur = currencies?.find((c) => c.code === "EUR");
    const target = currencies?.find((c) => c.code === currencyCode);
    if (!eur || !target) return null;
    return new Intl.NumberFormat("en-EN", { style: "currency", currency: target.code }).format(
      calculateCurrencyPrice(target, eur, amount ?? 0),
    );
  };

  const total = yachts.reduce((sum: number, y: any) => sum + (y?.pricing?.totalPrice ?? 0), 0);
  // Offer "value" is the AVERAGE across options (mirrors the offers-list Value column),
  // not the sum — the client picks one yacht, so the sum overstates the offer.
  const average = yachts.length ? total / yachts.length : 0;

  const remove = (index: number) => {
    const next = [...(getValues("yachts") ?? [])];
    next.splice(index, 1);
    setValue("yachts", next, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <aside className={sticky ? "lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-8rem)]" : undefined}>
      <div className="bg-white border rounded-lg border-blue-100 overflow-hidden flex flex-col max-h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-blue-50">
          <h3 className="text-sm font-semibold">Offer shortlist</h3>
          <span className="text-xs font-semibold bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">
            {yachts.length}
          </span>
        </div>
        {yachts.length === 0 ? (
          <p className="text-sm text-slate-500 p-4">
            Add yachts from search — the first becomes the "top pick". Up to ~4 works best.
          </p>
        ) : (
          <>
            <div className="flex flex-col divide-y divide-blue-50 overflow-y-auto min-h-0">
              {yachts.map((y: any, i: number) => (
                <div key={`${y?.yacht?.id ?? i}-${i}`} className="flex items-center gap-3 px-3 py-2.5">
                  {y?.yacht?.media?.[0] ? (
                    <img src={y.yacht.media[0]} alt="" className="w-11 h-9 rounded object-cover shrink-0" />
                  ) : (
                    <span className="w-11 h-9 rounded bg-slate-200 shrink-0" />
                  )}
                  <div className="min-w-0 grow">
                    <div className="flex items-center gap-1.5">
                      {i === 0 && (
                        <span className="text-[10px] font-semibold uppercase bg-amber-100 text-amber-700 rounded px-1 py-0.5 shrink-0">
                          Top
                        </span>
                      )}
                      <span className="text-sm font-medium truncate">{y?.yacht?.name || "Yacht"}</span>
                    </div>
                    <span className="text-xs text-slate-500">{toCurrency(y?.pricing?.totalPrice ?? 0) ?? ""}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="text-slate-400 hover:text-slate-700 shrink-0"
                    aria-label={`Remove ${y?.yacht?.name ?? "yacht"}`}
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center px-4 py-2.5 border-t border-blue-50 text-sm">
              <span className="text-slate-500">Average value</span>
              <span className="font-semibold">{toCurrency(average) ?? "—"}</span>
            </div>
            <div className="p-3 border-t border-blue-50">
              <button
                type="button"
                onClick={() => steps.setStep("Summary")}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 text-white text-sm font-medium py-2 hover:bg-blue-700"
              >
                Configure {yachts.length} yacht{yachts.length === 1 ? "" : "s"} <ArrowRight size={15} />
              </button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
